class CompareUtil {
  /**
   * Returns an array of the selected compare products from localStorage
   * @returns {Array}
   */
  static getSelectedProducts() {
    const compareProducts = theme.storageUtil.get('compare-products', true);
    return compareProducts || [];
  }

  /**
   * Sets an array of the selected compare products to localStorage
   * @param {Array} compareProducts - An array of product objects to compare
   */
  static setSelectedProducts(compareProducts) {
    theme.storageUtil.set('compare-products', compareProducts);
    document.dispatchEvent(new CustomEvent('on:compare-products:updated'));
  }

  /**
   * Adds a product id and url to the compare array in localStorage
   * @param {string} productId - the product id
   * @param {string} productUrl - the product url
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static addToCompare(productId, productUrl, updateDom) {
    const compareProducts = CompareUtil.getSelectedProducts().filter((product) =>
      product.id === productId || product.url.split('?')[0] !== productUrl.split('?')[0]);
    if (!compareProducts.some((product) => product.id === productId)) {
      compareProducts.push({ id: productId, url: productUrl });
    }
    CompareUtil.setSelectedProducts(compareProducts);
    CompareUtil.updateCompareCounters(compareProducts.length);

    if (updateDom) {
      CompareUtil.updateCompareCheckboxes();
    }
  }

  /**
   * Removes a product from the compare array in localStorage
   * @param {string} productId - the product id
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static removeFromCompare(productId, updateDom) {
    let compareProducts = CompareUtil.getSelectedProducts();
    if (compareProducts) {
      compareProducts = compareProducts.filter((product) => product.id !== productId);
    }
    CompareUtil.setSelectedProducts(compareProducts);
    CompareUtil.updateCompareCounters(compareProducts.length);

    if (updateDom) {
      CompareUtil.updateCompareCheckboxes();

      // Remove the product from the compare basket
      theme.elementUtil.remove(document.getElementById(`compare-basket-${productId}`));

      // Remove the product from the compare modal
      theme.elementUtil.remove(document.querySelectorAll(`.compare-col--${productId}`));
    }
  }

  /**
   * Removes a product from the compare array in localStorage
   * @param {boolean} updateDom - whether the dom should be updated or not
   */
  static clearCompare(updateDom) {
    const compareProducts = CompareUtil.getSelectedProducts();
    if (compareProducts) {
      compareProducts.forEach((product) => {
        CompareUtil.removeFromCompare(product.id, updateDom);
      });
    }
  }

  /**
   * Set whether the detected checkboxes should be checked or not
   */
  static updateCompareCheckboxes() {
    const compareProducts = CompareUtil.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      const isPrefixbox = Boolean(checkbox.closest('.prefixbox-product'));
      const selected = compareProducts.find((product) => {
        if (product.id === checkbox.dataset.productId) return true;
        if (!isPrefixbox) return false;
        try {
          return new URL(product.url, window.location.origin).pathname
            === new URL(checkbox.dataset.productUrl, window.location.origin).pathname;
        } catch (error) {
          return false;
        }
      });
      checkbox.checked = Boolean(selected);
      if (isPrefixbox && selected) checkbox.dataset.productId = selected.id;
    });
  }

  /** Restores theme compare controls on product cards replaced by XCloud results. */
  static restoreXCloudCompareControls(label) {
    if (document.querySelector('facet-filters')?.dataset.xcloudState !== 'ready') return false;

    let restored = false;
    document.querySelectorAll('.main-products-grid product-card:not(.card--product-compare)').forEach((card) => {
      const productId = card.dataset.productId;
      const productLink = card.querySelector('.card-link[href]');
      const media = card.querySelector('.card__media');
      if (!/^\d+$/.test(productId || '') || !productLink || !media || card.querySelector('.card__compare')) return;

      const url = new URL(productLink.href, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\//i.test(url.pathname)) return;

      const compare = document.createElement('div');
      compare.className = 'card__compare no-js-hidden text-sm';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'checkbox checkbox--compare js-compare-checkbox';
      checkbox.id = `compare-${productId}`;
      checkbox.dataset.productId = productId;
      checkbox.dataset.productUrl = url.pathname;
      const checkboxLabel = document.createElement('label');
      checkboxLabel.htmlFor = checkbox.id;
      checkboxLabel.textContent = label;
      compare.append(checkbox, checkboxLabel);
      let swatchRow = media.querySelector('.card__swatch-row');
      if (!swatchRow) {
        swatchRow = document.createElement('div');
        swatchRow.className = 'card__swatch-row';
        const swatches = media.querySelector('.card__color-swatches');
        if (swatches) {
          swatches.before(swatchRow);
          swatchRow.append(swatches);
        } else {
          media.append(swatchRow);
        }
      }
      swatchRow.append(compare);
      card.classList.add('card--product-compare');
      restored = true;
    });
    return restored;
  }

  /**
   * Returns a count of the number of currently in the compare basket
   * @returns {number}
   */
  static getCompareCount() {
    return CompareUtil.getSelectedProducts().length;
  }

  /**
   * Updates counters on the page with the count of products in the compare basket
   * @param {number} pCount - optionally can pass in the count of products to update
   */
  static updateCompareCounters(pCount) {
    const count = pCount || CompareUtil.getCompareCount();
    document.querySelectorAll('.js-compare-counter').forEach((counter) => {
      counter.innerText = count;
    });
  }
}

if (!customElements.get('compare-drawer')) {
  class CompareDrawer extends HTMLElement {
    constructor() {
      super();
      this.compareBasketArea = this.querySelector('.js-compare-basket');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.doCompareButton = this.querySelector('.js-trigger-compare');
      this.clearButton = this.querySelector('.js-clear-compare');
      this.maxCompareDesktop = parseInt(this.dataset.maxCompare, 10);
      this.maxCompareMobile = parseInt(this.dataset.maxCompareMobile || this.dataset.maxCompare, 10);
      this.pageType = this.dataset.pageType;
      this.currentProductId = this.dataset.currentProductId;
      this.mobileMediaQuery = window.matchMedia('(max-width: 768.98px)');
      this.compareModal = document.querySelector('.js-compare-modal');
      this.basketCache = new Map();
      this.renderId = 0;
      const barExpanded = theme.storageUtil.get('compare-bar-expanded', true) !== false;
      this.querySelector('.js-compare-bar-toggle')?.setAttribute('aria-expanded', String(barExpanded));
      const barContent = this.querySelector('.compare-bar__inner');
      if (barContent) barContent.hidden = !barExpanded;
      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      this.barAnimation?.cancel();
      this.renderId += 1;
      this.barResizeObserver?.disconnect();
      document.documentElement.style.removeProperty('--compare-bar-height');
      this.xCloudCompareObserver?.disconnect();
      this.prefixboxCompareObserver?.disconnect();
      if (this.pageType === 'search') delete document.documentElement.dataset.prefixboxCompare;
      window.removeEventListener('pageshow', CompareUtil.updateCompareCheckboxes);
      document.removeEventListener('click', this.clickHandler);
      document.removeEventListener('change', this.changeHandler);
      document.removeEventListener('on:facet-filters:updated', CompareUtil.updateCompareCheckboxes);
      document.removeEventListener('on:compare-products:updated', this.compareProductsUpdatedHandler);
      document.removeEventListener('on:variant:change', this.compareProductsUpdatedHandler);
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.mediaQueryHandler);
      } else {
        this.mobileMediaQuery.removeListener(this.mediaQueryHandler);
      }
    }

    init() {
      if (this.pageType === 'search') {
        this.prefixboxCompareObserver = new MutationObserver((records) => {
          const addedCompareCards = records.some((record) => [...record.addedNodes].some((node) =>
            node.nodeType === Node.ELEMENT_NODE && (
              node.matches('.prefixbox-product, .js-compare-checkbox')
              || node.querySelector('.prefixbox-product, .js-compare-checkbox')
            )));
          if (addedCompareCards) CompareUtil.updateCompareCheckboxes();
        });
        this.prefixboxCompareObserver.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('pageshow', CompareUtil.updateCompareCheckboxes);
      }
      const xCloudResults = document.querySelector('[data-cloud-search-reload]');
      if (xCloudResults) {
        this.xCloudCompareObserver = new MutationObserver((records) => {
          if (!records.some((record) => [...record.addedNodes].some((node) =>
            node.nodeType === Node.ELEMENT_NODE && (node.matches('product-card') || node.querySelector('product-card'))))) return;
          if (CompareUtil.restoreXCloudCompareControls(this.dataset.compareLabel)) {
            CompareUtil.updateCompareCheckboxes();
          }
        });
        this.xCloudCompareObserver.observe(xCloudResults, { childList: true, subtree: true });
        if (CompareUtil.restoreXCloudCompareControls(this.dataset.compareLabel)) {
          CompareUtil.updateCompareCheckboxes();
        }
      }
      CompareUtil.updateCompareCheckboxes();
      CompareUtil.updateCompareCounters();
      this.updateCompareMaxCounters();
      this.barResizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--compare-bar-height', this.hidden ? '0px' : `${this.getBoundingClientRect().height}px`);
      });
      this.barResizeObserver.observe(this);
      this.updateBar();

      this.compareModal.querySelector('.details-modal').classList.remove('hidden');
    }

    bindEvents() {
      this.clickHandler = this.handleClick.bind(this);
      this.changeHandler = this.handleChange.bind(this);
      this.mediaQueryHandler = () => {
        this.updateCompareMaxCounters();
        this.updateBar();
      };
      this.compareProductsUpdatedHandler = this.updateBar.bind(this);

      document.addEventListener('click', this.clickHandler);
      document.addEventListener('change', this.changeHandler);
      document.addEventListener('on:facet-filters:updated', CompareUtil.updateCompareCheckboxes);
      document.addEventListener('on:compare-products:updated', this.compareProductsUpdatedHandler);
      if (this.pageType === 'product') {
        document.addEventListener('on:variant:change', this.compareProductsUpdatedHandler);
      }
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.mediaQueryHandler);
      } else {
        this.mobileMediaQuery.addListener(this.mediaQueryHandler);
      }
      this.doCompareButton.addEventListener('click', this.handleDoCompareClick.bind(this));
      this.clearButton.addEventListener('click', this.handleClearCompareClick.bind(this));
    }

    /**
     * Returns the active compare limit for the current viewport.
     * @returns {number}
     */
    getMaxCompare() {
      return this.mobileMediaQuery.matches ? this.maxCompareMobile : this.maxCompareDesktop;
    }

    /**
     * Updates max compare counters on the page for the current viewport.
     */
    updateCompareMaxCounters() {
      const maxCompare = this.getMaxCompare();
      document.querySelectorAll('.js-compare-max-counter').forEach((counter) => {
        counter.innerText = maxCompare;
      });
    }

    updateBar() {
      const grid = document.querySelector('.main-products-grid');
      let compareEnabled = grid?.dataset.compare !== 'false';
      if (this.pageType === 'search' || this.pageType === 'product') {
        compareEnabled = true;
        if (this.pageType === 'search') document.documentElement.dataset.prefixboxCompare = String(compareEnabled);
      }
      this.hidden = CompareUtil.getCompareCount() === 0 || !compareEnabled;
      if (this.pageType === 'product') {
        document.querySelectorAll('.js-pdp-compare').forEach((control) => {
          control.hidden = !compareEnabled;
        });
        CompareUtil.updateCompareCheckboxes();
      }
      if (this.hidden) document.documentElement.style.setProperty('--compare-bar-height', '0px');
      this.updateBasketState();
      this.renderCompareBasket();
    }

    handleClick(evt) {
      const toggleButton = evt.target.closest('.js-compare-bar-toggle');
      if (toggleButton && this.contains(toggleButton)) {
        const expanded = toggleButton.getAttribute('aria-expanded') !== 'true';
        toggleButton.setAttribute('aria-expanded', String(expanded));
        if (!Shopify.designMode) theme.storageUtil.set('compare-bar-expanded', expanded);
        const content = this.querySelector('.compare-bar__inner');
        const startHeight = content.hidden ? 0 : content.getBoundingClientRect().height;
        const startOpacity = content.hidden ? 0 : Number(getComputedStyle(content).opacity);
        this.barAnimation?.cancel();
        content.hidden = false;
        const endHeight = expanded ? content.scrollHeight : 0;
        const animation = content.animate([
          { height: `${startHeight}px`, opacity: startOpacity, overflow: 'hidden' },
          { height: `${endHeight}px`, opacity: expanded ? 1 : 0, overflow: 'hidden' }
        ], {
          duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
        });
        this.barAnimation = animation;
        animation.onfinish = () => {
          if (this.barAnimation !== animation) return;
          content.hidden = !expanded;
          this.barAnimation = null;
        };
        return;
      }
      const addButton = evt.target.closest('.js-compare-suggestion-add');
      if (addButton && this.contains(addButton)) {
        if (CompareUtil.getCompareCount() < this.getMaxCompare()) {
          CompareUtil.addToCompare(addButton.dataset.productId, addButton.dataset.productUrl, true);
          (this.doCompareButton.disabled ? this.clearButton : this.doCompareButton).focus();
        }
        return;
      }
      const removeButton = evt.target.closest('.js-compare-basket-remove');
      if (!removeButton || !this.contains(removeButton)) return;
      const nextFocus = removeButton.closest('.card--compare-basket')?.nextElementSibling?.querySelector('.js-compare-basket-remove');
      CompareUtil.removeFromCompare(removeButton.dataset.productId.toString(), true);
      if (CompareUtil.getCompareCount() > 0) (nextFocus || this.clearButton).focus();
      else document.querySelector('#compare-switch, .js-compare-checkbox')?.focus();
    }

    /**
     * Handles a 'change' event for comparison checkboxes
     * @param {object} evt - Event object
     */
    async handleChange(evt) {
      if (evt.target.classList.contains('js-compare-checkbox')) {
        const checkbox = evt.target;
        if (checkbox.closest('.prefixbox-product') && !checkbox.dataset.compareIdResolved) {
          checkbox.disabled = true;
          try {
            const productUrl = new URL(checkbox.dataset.productUrl, window.location.origin);
            if (productUrl.origin !== window.location.origin) throw new Error('Invalid product URL');
            productUrl.search = '';
            productUrl.pathname = `${productUrl.pathname.replace(/\/$/, '')}.js`;
            const response = await fetch(productUrl, { signal: AbortSignal.timeout(8000) });
            if (!response.ok) throw new Error('Product unavailable');
            const product = await response.json();
            if (!product.id) throw new Error('Product ID unavailable');
            checkbox.dataset.productId = String(product.id);
            checkbox.dataset.compareIdResolved = 'true';
          } catch (error) {
            checkbox.checked = false;
            return;
          } finally {
            checkbox.disabled = false;
          }
        }
        const productData = evt.target.dataset;
        const maxCompare = this.getMaxCompare();

        if (evt.target.checked) {
          if (CompareUtil.getCompareCount() < maxCompare) {
            CompareUtil.addToCompare(
              productData.productId.toString(),
              productData.productUrl,
              false
            );
          } else {
            alert(`${theme.strings.compare.limit.replace('[quantity]', maxCompare)}`); // eslint-disable-line
            evt.target.checked = false;
          }
        } else {
          CompareUtil.removeFromCompare(productData.productId.toString(), false);
        }

        this.updateBasketState();
      }
    }

    /**
     * Handle click on the Compare button
     */
    handleDoCompareClick() {
      if (this.doCompareButton.disabled) return;
      this.compareModal.dispatchEvent(new CustomEvent('on:product-compare-modal:open', {
        detail: { opener: this.doCompareButton }
      }));
    }

    handleClearCompareClick() {
      CompareUtil.clearCompare(true);
      document.querySelector('#compare-switch, .js-compare-checkbox')?.focus();
    }

    updateBasketState() {
      CompareUtil.updateCompareCounters();
      const count = Math.min(CompareUtil.getCompareCount(), this.getMaxCompare());
      this.dataset.compareCount = count;
      this.doCompareButton.disabled = count < 2;
      this.doCompareButton.setAttribute('aria-disabled', String(count < 2));
      this.querySelector('.js-compare-mobile-limit').hidden = !this.mobileMediaQuery.matches || CompareUtil.getCompareCount() <= this.getMaxCompare();
    }

    async renderCompareBasket() {
      const maxCompare = this.getMaxCompare();
      const products = CompareUtil.getSelectedProducts().slice(0, maxCompare);
      const selectionKey = `${maxCompare}:${products.map((product) => `${product.id}:${product.url}`).join(',')}`;
      this.compareBasketArea.style.setProperty('--compare-slots', maxCompare);
      if (this.basketSelectionKey === selectionKey) return;
      const renderId = ++this.renderId;
      this.basketSelectionKey = selectionKey;
      const suggestion = this.querySelector('.js-compare-suggestion');
      const showSuggestion = suggestion && CompareUtil.getCompareCount() < maxCompare
        && !CompareUtil.getSelectedProducts().some((product) => product.id === this.currentProductId);
      if (!products.length && !showSuggestion) {
        this.compareBasketArea.replaceChildren();
        this.loadingSpinner.hidden = true;
        return;
      }
      this.loadingSpinner.hidden = this.compareBasketArea.children.length > 0;
      try {
        const html = await CompareDrawer.getBasketProducts(products, this.basketCache);
        if (renderId !== this.renderId || !this.isConnected) return;
        const cards = products.map((product, index) => {
          const existing = document.getElementById(`compare-basket-${product.id}`);
          if (existing && this.contains(existing)) return existing;
          const template = document.createElement('template');
          template.innerHTML = html[index] || '';
          return template.content.querySelector('.card--compare-basket');
        }).filter(Boolean);
        if (showSuggestion) {
          cards.push(this.compareBasketArea.querySelector('.card--compare-suggestion')
            || suggestion.content.firstElementChild.cloneNode(true));
        }
        const emptySlot = this.querySelector('.js-compare-empty-slot');
        const existingSlots = [...this.compareBasketArea.querySelectorAll('.compare-bar__empty-slot')];
        while (cards.length < maxCompare) {
          cards.push(existingSlots.shift() || emptySlot.content.firstElementChild.cloneNode(true));
        }
        cards.forEach((card, index) => {
          const current = this.compareBasketArea.children[index];
          if (current !== card) this.compareBasketArea.insertBefore(card, current || null);
        });
        [...this.compareBasketArea.children].forEach((card) => {
          if (!cards.includes(card)) card.remove();
        });
        if (html.some((item) => !item)) this.basketSelectionKey = null;
      } catch (error) {
        if (renderId === this.renderId) this.basketSelectionKey = null;
      } finally {
        if (renderId === this.renderId) this.loadingSpinner.hidden = true;
      }
    }

    /**
     * Fetches an array of html for each of the compare products, using the section rendering API
     * @param {Array} compareProducts - An array of product objects to compare
     */
    static async getBasketProducts(products, cache) {
      return Promise.all(products.map((product) => {
        const url = new URL(product.url, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        url.searchParams.set('sections', 'product-compare-basket');
        const key = url.href;
        if (!cache.has(key)) {
          const request = fetch(url, { signal: AbortSignal.timeout(8000) })
            .then((response) => {
              if (!response.ok) throw new Error('Product unavailable');
              return response.json();
            })
            .then((sections) => {
              if (!sections['product-compare-basket']) throw new Error('Compare basket unavailable');
              return sections['product-compare-basket'];
            })
            .catch(() => {
              cache.delete(key);
              return null;
            });
          cache.set(key, request);
        }
        return cache.get(key);
      }));
    }

  }

  customElements.define('compare-drawer', CompareDrawer);
}
